import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import {
  Box,
  Container,
  Typography,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Autocomplete,
  Avatar,
  Skeleton,
  Chip,
  CircularProgress,
  createFilterOptions
} from '@mui/material';
import { Search, LocationOn, Star, MyLocation } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AutoFitText from '../Common/AutoFitText';
import SkillLabel from '../Common/SkillLabel';
import VoiceSearchButton from '../Common/VoiceSearchButton';
import Api from '../../api/userApi';
import { mediaVariantUrl, mediaSrcSet } from '../../utils/mediaUrl';
import getSocketServerUrl from '../../utils/getSocketServerUrl';

const DEFAULT_SLOGAN = {
  mainHeading: 'Find the Right Workers',
  subheading: 'Top-Rated Workers in Your Area',
  smallDescription: 'Trained, experts, reliable!',
};

const MOBILE_PAGE_CREAM = '#F6EEE7';
const HERO_BANNER_BG = '#F6EEE7';
const HERO_CONTENT_MAX_WIDTH = '1400px';
const HERO_CONTENT_PX = { xs: 2, sm: 2.5, md: 5 }; // 40px horizontal padding on desktop

const parseHomeSloganBannerActive = (value) => {
  if (value === false || value === 0) return false;
  if (value === 'false' || value === '0') return false;
  return true;
};

const skillFilterOptions = createFilterOptions({
  matchFrom: 'any',
  stringify: (option) => option?.name || '',
});

// Worker-name suggestions: match anywhere in the name, cap the list so the
// dropdown stays focused on what the user typed instead of showing everyone.
const nameFilterOptions = createFilterOptions({
  matchFrom: 'any',
  trim: true,
  limit: 20,
  stringify: (option) => (typeof option === 'string' ? option : option?.name || ''),
});

const HIGHLIGHT_BROWN = '#8B4513';

/** Trust badge: real worker avatars, count pill, Happy Customers label, and stars.
 *  Pass `compact` for the smaller variant used inside the mobile banner card. */
const BannerHappyCustomersBadge = ({ compact = false }) => {
  const [trust, setTrust] = useState({ avatars: [], countLabel: '' });

  useEffect(() => {
    let cancelled = false;

    const fetchTrust = async () => {
      try {
        const res = await axios.get(`${Api.homeAssets}/hero-trust-banner`);
        if (cancelled) return;
        setTrust({
          avatars: Array.isArray(res.data?.avatars) ? res.data.avatars : [],
          countLabel: res.data?.countLabel || '',
        });
      } catch (_) {
        if (!cancelled) setTrust({ avatars: [], countLabel: '' });
      }
    };

    fetchTrust();

    return () => {
      cancelled = true;
    };
  }, []);

  const { avatars, countLabel } = trust;
  if (!avatars.length) return null;

  // Compact variant (used inside the mobile banner) shrinks every element so the
  // badge fits within the card without clipping.
  const avatarSize = compact ? 22 : 36;
  const overlap = compact ? '-8px' : '-12px';
  const countFont = compact ? 8 : 11;
  const labelFont = compact ? 9.5 : 13;
  const starFont = compact ? 10 : 15;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        alignSelf: 'flex-start',
        width: 'fit-content',
        maxWidth: '100%',
        mt: compact ? 0 : 1.25,
        bgcolor: '#fff',
        borderRadius: 999,
        px: compact ? 0.6 : 1.25,
        py: compact ? 0.4 : 0.75,
        boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
        border: '1px solid rgba(0,0,0,0.05)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        {avatars.map((item, index) => (
          <Avatar
            key={item.profilePicture || index}
            src={mediaVariantUrl(item.profilePicture, 'thumb')}
            alt={item.name ? `${item.name}` : ''}
            imgProps={{ loading: 'lazy', decoding: 'async', width: 128, height: 128 }}
            sx={{
              width: avatarSize,
              height: avatarSize,
              border: '2px solid #fff',
              ml: index === 0 ? 0 : overlap,
              zIndex: avatars.length - index,
              bgcolor: '#e8e8e8',
            }}
          />
        ))}
        {countLabel ? (
          <Box
            sx={{
              width: avatarSize,
              height: avatarSize,
              borderRadius: '50%',
              bgcolor: HIGHLIGHT_BROWN,
              color: '#fff',
              fontSize: countFont,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid #fff',
              ml: overlap,
              zIndex: 0,
              flexShrink: 0,
              lineHeight: 1,
            }}
          >
            {countLabel}
          </Box>
        ) : null}
      </Box>
      <Box sx={{ ml: compact ? 0.6 : 1, flexShrink: 0, width: 'max-content' }}>
        <Typography
          component="span"
          sx={{
            display: 'block',
            fontSize: labelFont,
            fontWeight: 600,
            color: '#1E1E1E',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
          }}
        >
          Happy Customers
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.15,
            mt: compact ? 0.15 : 0.35,
            width: 'fit-content',
          }}
        >
          {[0, 1, 2, 3, 4].map((i) => (
            <Star key={i} sx={{ fontSize: starFont, color: HIGHLIGHT_BROWN }} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

/** Split heading so only the word "Right" is highlighted (matches reference). */
const renderBannerHeading = (text) => {
  const highlight = 'Right';
  const idx = text.indexOf(highlight);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <Box component="span" sx={{ color: HIGHLIGHT_BROWN }}>
        {highlight}
      </Box>
      {text.slice(idx + highlight.length)}
    </>
  );
};

/** Heading with measure refs so subheading can align under the word "Right". */
const renderBannerHeadingMeasured = (text, prefixRef, rightWordRef) => {
  const highlight = 'Right Workers';
  const idx = text.indexOf(highlight);
  if (idx === -1) return { measurable: false, nodes: renderBannerHeading(text) };
  return {
    measurable: true,
    nodes: (
      <>
        <span ref={prefixRef}>{text.slice(0, idx)}</span>
        <Box component="span" ref={rightWordRef} sx={{ color: HIGHLIGHT_BROWN }}>
          Right
        </Box>
        <span> Workers</span>
        {text.slice(idx + highlight.length)}
      </>
    ),
  };
};

/** Scale banner text to fill a target width (full search card or width under "Right"). */
const BannerFillHeading = ({
  children,
  minFontSize = 32,
  maxFontSize = 72,
  targetWidthPx = null,
  onAfterFit = null,
  fontWeight = 800,
  color = '#1E1E1E',
}) => {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    let ro = null;

    const fit = () => {
      const container = el.parentElement;
      const targetWidth =
        typeof targetWidthPx === 'number' && targetWidthPx > 0
          ? targetWidthPx
          : container?.clientWidth || el.clientWidth;
      if (!targetWidth) return;

      let size = maxFontSize;
      el.style.fontSize = `${size}px`;

      while (el.scrollWidth > targetWidth && size > minFontSize) {
        size -= 1;
        el.style.fontSize = `${size}px`;
      }

      while (el.scrollWidth < targetWidth - 1 && size < maxFontSize) {
        size += 1;
        el.style.fontSize = `${size}px`;
        if (el.scrollWidth > targetWidth) {
          size -= 1;
          el.style.fontSize = `${size}px`;
          break;
        }
      }

      onAfterFit?.();
    };

    fit();
    ro = new ResizeObserver(fit);
    if (el.parentElement) ro.observe(el.parentElement);
    window.addEventListener('resize', fit);

    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [children, minFontSize, maxFontSize, targetWidthPx, onAfterFit]);

  return (
    <Box
      ref={ref}
      component="span"
      sx={{
        display: 'block',
        width: '100%',
        whiteSpace: 'nowrap',
        fontWeight,
        lineHeight: 1.08,
        letterSpacing: '-0.02em',
        color,
      }}
    >
      {children}
    </Box>
  );
};

/** Highlight "reliable!" in small description when present. */
const renderBannerSmallDescription = (text) => {
  const match = /(reliable!)/i.exec(text);
  if (!match) return text;
  const idx = match.index;
  const word = match[1];
  return (
    <>
      {text.slice(0, idx)}
      <Box component="span" sx={{ color: HIGHLIGHT_BROWN, fontWeight: 600 }}>
        {word}
      </Box>
      {text.slice(idx + word.length)}
    </>
  );
};

const HomeHeroSearch = ({
  location,
  setLocation,
  service,
  setService,
  skillInputText,
  setSkillInputText,
  searchName,
  setSearchName,
  minTeamSize,
  setMinTeamSize,
  services,
  workerNames,
  villages,
  onSearch,
  onVoiceSearch,
  onNearMe,
  nearMeLoading = false,
  onOpenLocationScreen,
  onOpenServiceSearchScreen
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Tablet widths can be tight; hide extra fields there to prevent overlap/clipping.
  const isTabletOrDown = useMediaQuery(theme.breakpoints.down('md'));

  // Voice search hooks into the existing service/skill field: interim results
  // fill the input live (so users can edit), and the final result triggers the
  // same search as manual typing.
  const handleVoiceInterim = useCallback((text) => {
    if (setSkillInputText) setSkillInputText(text);
    if (setSearchName) setSearchName('');
  }, [setSkillInputText, setSearchName]);

  const handleVoiceFinal = useCallback((text) => {
    if (onVoiceSearch) onVoiceSearch(text);
  }, [onVoiceSearch]);

  // Small, unobtrusive "Near Me" chip. GPS is requested only when tapped (via
  // onNearMe in the parent); it never blocks the normal Location/Skill/Name
  // search. No map is used. Defined early so every search-bar variant below can
  // render it without a temporal-dead-zone reference error.
  const renderNearMeChip = (sx = {}) =>
    onNearMe ? (
      <Chip
        icon={
          nearMeLoading
            ? <CircularProgress size={14} sx={{ color: HIGHLIGHT_BROWN }} />
            : <MyLocation />
        }
        label={nearMeLoading ? 'Locating…' : 'Near Me'}
        onClick={nearMeLoading ? undefined : onNearMe}
        variant="outlined"
        disabled={nearMeLoading}
        size="small"
        sx={{
          bgcolor: '#fff',
          color: '#323232',
          borderColor: '#E0D6CC',
          fontWeight: 600,
          '& .MuiChip-icon': { color: HIGHLIGHT_BROWN },
          ...sx
        }}
      />
    ) : null;

  const [slogan, setSlogan] = useState(DEFAULT_SLOGAN);
  const [bannerImageUrl, setBannerImageUrl] = useState('');
  const [bannerBackgroundImageUrl, setBannerBackgroundImageUrl] = useState('');
  const [homeSloganBannerActive, setHomeSloganBannerActive] = useState(true);
  const [bannerConfigReady, setBannerConfigReady] = useState(false);
  // Track when the hero banner image has actually decoded so we can fade it in
  // over a skeleton (prevents a blank gap / layout shift while it downloads).
  const [heroImgLoaded, setHeroImgLoaded] = useState(false);

  useEffect(() => {
    setHeroImgLoaded(false);
  }, [bannerImageUrl]);

  const applySloganBannerData = useCallback((data) => {
    setSlogan({
      mainHeading: data?.mainHeading || DEFAULT_SLOGAN.mainHeading,
      subheading: data?.subheading || DEFAULT_SLOGAN.subheading,
      smallDescription: data?.smallDescription || DEFAULT_SLOGAN.smallDescription,
    });
    setBannerImageUrl(data?.hasImage && data?.image ? data.image : '');
    setBannerBackgroundImageUrl(
      data?.hasBackgroundImage && data?.backgroundImage ? data.backgroundImage : ''
    );
    setHomeSloganBannerActive(parseHomeSloganBannerActive(data?.homeSloganBannerActive));
    setBannerConfigReady(true);
  }, []);

  const fetchSloganBanner = useCallback(async () => {
    try {
      const res = await axios.get(`${Api.homeAssets}/home_slogan_banner`);
      applySloganBannerData(res.data);
    } catch (_) {
      applySloganBannerData({
        ...DEFAULT_SLOGAN,
        homeSloganBannerActive: true,
        hasImage: false,
        hasBackgroundImage: false,
      });
    }
  }, [applySloganBannerData]);

  useEffect(() => {
    fetchSloganBanner();
  }, [fetchSloganBanner]);

  useEffect(() => {
    const bannerSocket = io(getSocketServerUrl(), {
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
    });

    bannerSocket.on('homeSloganBannerUpdated', fetchSloganBanner);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchSloganBanner();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      bannerSocket.off('homeSloganBannerUpdated', fetchSloganBanner);
      bannerSocket.close();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [fetchSloganBanner]);

  const { mainHeading, subheading, smallDescription } = slogan;
  const showModernHeroBanner =
    homeSloganBannerActive && Boolean(bannerImageUrl);

  const modernHeroBackground = bannerBackgroundImageUrl
    ? `linear-gradient(90deg, rgba(246, 238, 231, 0.92) 0%, rgba(240, 230, 222, 0.85) 100%), url(${bannerBackgroundImageUrl}) center center / cover no-repeat`
    : `linear-gradient(90deg, ${HERO_BANNER_BG} 0%, #F0E6DE 100%)`;

  // Mobile banner uses the exact same background as web for consistent brightness.
  const mobileBannerBackground = modernHeroBackground;

  const prefixMeasureRef = useRef(null);
  const rightWordMeasureRef = useRef(null);
  const [subheadingEndWidth, setSubheadingEndWidth] = useState(null);

  const measureSubheadingUnderRight = useCallback(() => {
    const prefix = prefixMeasureRef.current;
    const right = rightWordMeasureRef.current;
    if (prefix && right) {
      setSubheadingEndWidth(Math.ceil(prefix.offsetWidth + right.offsetWidth));
    }
  }, []);

  const bannerHeadingParts = renderBannerHeadingMeasured(
    mainHeading,
    prefixMeasureRef,
    rightWordMeasureRef
  );

  useEffect(() => {
    setSubheadingEndWidth(null);
  }, [mainHeading]);

  const mobileHeroText = (
    <>
      <Typography
        variant="h5"
        component="h1"
        align="center"
        noWrap
        sx={{
          fontWeight: 700,
          mb: 1,
          px: { xs: 2, sm: 4 },
          lineHeight: 1.35,
          fontSize: 'clamp(1.75rem, 6vw, 2.1rem)',
          overflow: 'hidden',
          textOverflow: 'clip',
        }}
      >
        <AutoFitText minFontSize={16}>{mainHeading}</AutoFitText>
      </Typography>
      <Typography
        variant="subtitle1"
        align="center"
        noWrap
        sx={{
          mb: 0.75,
          fontWeight: 500,
          px: { xs: 2, sm: 4 },
          lineHeight: 1.3,
          fontSize: 'clamp(1.05rem, 3.8vw, 1.2rem)',
          overflow: 'hidden',
          textOverflow: 'clip',
          maxWidth: { xs: '85%', sm: '80%' },
          mx: 'auto',
        }}
      >
        <AutoFitText minFontSize={12}>{subheading}</AutoFitText>
      </Typography>
      <Typography
        variant="subtitle2"
        noWrap
        sx={{
          mb: 3,
          fontWeight: 600,
          px: { xs: 2, sm: 4 },
          lineHeight: 1.3,
          maxWidth: { xs: '60%', sm: '55%', md: '50%' },
          fontSize: 'clamp(0.9rem, 3.2vw, 1.05rem)',
          overflow: 'hidden',
          textOverflow: 'clip',
          mx: 'auto',
        }}
        align="center"
      >
        <AutoFitText minFontSize={11}>{smallDescription}</AutoFitText>
      </Typography>
    </>
  );

  const mobileSearchBar = (
    <Box>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        mt: 1.5,
        bgcolor: '#ffffff',
        borderRadius: 3,
        px: 1,
        py: 0.5,
        boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
        overflow: 'hidden',
      }}
    >
      <Box
        onClick={onOpenLocationScreen}
        sx={{
          mr: 0.5,
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          bgcolor: '#fff4e3',
          borderRadius: 2,
          pl: 0.5,
          pr: location ? 1 : 0.5,
          py: 0.25,
          maxWidth: 128,
          '&:hover': { bgcolor: '#ffe0b2' },
        }}
      >
        <IconButton
          aria-label="search location"
          sx={{ p: 1 }}
        >
          <LocationOn sx={{ color: '#ff7a1a' }} />
        </IconButton>
        {location ? (
          <Typography
            variant="caption"
            noWrap
            sx={{ fontWeight: 700, color: '#323232', maxWidth: 72 }}
          >
            {location}
          </Typography>
        ) : null}
      </Box>

      <Box
        onClick={onOpenServiceSearchScreen}
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          ml: 1,
          borderLeft: '1px solid #eee',
          pl: 1.5,
          minHeight: 40,
          cursor: 'text',
        }}
      >
        <Search sx={{ color: '#757575', mr: 1 }} />
        <Typography
          variant="body2"
          sx={{
            color: (service || searchName) ? '#212121' : '#9e9e9e',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {service ? <SkillLabel name={service} /> : (searchName || 'Search your service')}
        </Typography>
      </Box>

      <Box
        sx={{ display: 'flex', alignItems: 'center', pl: 0.5, borderLeft: '1px solid #eee' }}
        onClick={(e) => e.stopPropagation()}
      >
        <VoiceSearchButton
          onInterimResult={handleVoiceInterim}
          onFinalResult={handleVoiceFinal}
          iconColor="#ff7a1a"
          iconFontSize={22}
        />
      </Box>
    </Box>
      {/* Near Me — opt-in GPS distance search (no map). */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
        {renderNearMeChip()}
      </Box>
    </Box>
  );

  const mobileHeroShell = (
    <Box
      sx={{
        background: '#DDD0C8',
        color: '#323232',
        pt: 8,
        pb: 4,
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          textAlign: 'center',
          px: { xs: 2.5, sm: 3 },
        }}
      >
        {mobileHeroText}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <BannerHappyCustomersBadge />
        </Box>
        {mobileSearchBar}
      </Container>
    </Box>
  );

  // Spacious dropdown styles for desktop (Sulekha-inspired)
  const desktopDropdownSlotProps = {
    paper: {
      sx: {
        mt: 1,
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        minWidth: 320,
        border: '1px solid #eee',
        overflow: 'hidden',
      }
    },
    listbox: {
      sx: {
        maxHeight: 400,
        py: 1,
        '& .MuiAutocomplete-option': {
          px: 2.5,
          py: 1.4,
          minHeight: 46,
          fontSize: '0.975rem',
          lineHeight: 1.6,
          borderBottom: '1px solid #f5f5f5',
          transition: 'background-color 0.15s ease',
          '&:last-child': {
            borderBottom: 'none',
          },
          '&:hover': {
            backgroundColor: '#f8f5f2',
          },
          '&[aria-selected="true"]': {
            backgroundColor: '#ede7e0',
            '&:hover': {
              backgroundColor: '#e0d8cf',
            },
          },
        },
        '& .MuiAutocomplete-groupLabel': {
          fontWeight: 700,
          fontSize: '0.8rem',
          color: '#888',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          px: 2.5,
          pt: 2,
          pb: 0.75,
          lineHeight: 1.5,
        },
      }
    }
  };

  const desktopSearchBar = (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2, gap: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            // Compact overall width (match earlier search bar feel)
            maxWidth: { xs: '100%', md: 740 },
            mx: 'auto',
            mt: 2,
            bgcolor: 'white',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            height: 56 // match standard MUI text field height for consistent desktop layout
          }}
        >
          <Box
            sx={{
              flex: 1,
              // Location should be narrower
              minWidth: 140,
              px: 1,
              display: 'flex',
              alignItems: 'center',
              height: '100%'
            }}
          >
            <Autocomplete
              freeSolo
              options={villages || []}
              value={location || ''}
              onInputChange={(_, newInputValue) => {
                // Allow typing + searching the villages list
                if (setLocation) {
                  setLocation(newInputValue || '');
                }
              }}
              onChange={(_, newValue) => {
                const value =
                  typeof newValue === 'string'
                    ? newValue
                    : (newValue || '');
                if (setLocation) {
                  setLocation(value);
                }
              }}
              slotProps={desktopDropdownSlotProps}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Location"
                  variant="standard"
                  sx={{
                    height: '100%',
                    width: '100%',
                    '& .MuiInput-underline:before': { borderBottom: 'none' },
                    '& .MuiInput-underline:after': { borderBottom: 'none' },
                    '& .MuiInputBase-root': {
                      height: '100%',
                      alignItems: 'center'
                    },
                    '& .MuiInputBase-input': {
                      padding: 0,
                      fontSize: '1rem',
                      color: location ? '#323232' : '#999'
                    },
                    '& .MuiAutocomplete-input': {
                      padding: 0
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start" sx={{ mr: 1 }}>
                        <LocationOn sx={{ color: '#323232' }} />
                      </InputAdornment>
                    )
                  }}
                />
              )}
              sx={{
                width: '100%',
                height: '100%',
                '& .MuiAutocomplete-inputRoot': {
                  padding: '0 !important',
                  height: '100%'
                },
                '& .MuiInputBase-root': {
                  height: '100%',
                  alignItems: 'center'
                }
              }}
            />
          </Box>

          <Box
            sx={{
              // Skill dropdown compact width
              minWidth: 120,
              px: 1,
              display: 'flex',
              alignItems: 'center',
              height: '100%'
            }}
          >
            <Autocomplete
              freeSolo
              options={services || []}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option?.name || '';
              }}
              filterOptions={skillFilterOptions}
              value={services?.find(s => s.name === service) || null}
              inputValue={skillInputText !== undefined ? skillInputText : (service || '')}
              onInputChange={(event, newInputValue) => {
                // Track whatever the user types so the parent can
                // include it in navigation even without a dropdown selection.
                if (setSkillInputText) {
                  setSkillInputText(newInputValue || '');
                }
              }}
              onChange={(event, newValue) => {
                const selected = typeof newValue === 'string' ? newValue : (newValue?.name || '');
                setService(selected);
                if (setSkillInputText) {
                  setSkillInputText(selected);
                }
              }}
              slotProps={desktopDropdownSlotProps}
              renderOption={(props, option) => (
                <li {...props}>
                  <SkillLabel name={option?.name || ''} />
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Skill"
                  variant="standard"
                  inputProps={{
                    ...params.inputProps,
                    className: `notranslate ${params.inputProps?.className || ''}`.trim(),
                    translate: 'no'
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.defaultMuiPrevented = true;
                      if (onSearch) onSearch();
                    }
                  }}
                  sx={{
                    height: '100%',
                    '& .MuiInput-underline:before': {
                      borderBottom: 'none'
                    },
                    '& .MuiInput-underline:after': {
                      borderBottom: 'none'
                    },
                    '& .MuiInputBase-input': {
                      padding: 0,
                      fontSize: '1rem',
                      color: (service || skillInputText) ? '#323232' : '#999'
                    },
                    '& .MuiAutocomplete-input': {
                      padding: 0
                    }
                  }}
                />
              )}
              sx={{
                minWidth: 150,
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                '& .MuiAutocomplete-inputRoot': {
                  padding: '0 !important',
                  height: '100%'
                },
                '& .MuiInputBase-root': {
                  height: '100%',
                  alignItems: 'center'
                }
              }}
            />
          </Box>

          <Autocomplete
            freeSolo
            options={workerNames || []}
            value={null}
            inputValue={searchName || ''}
            filterOptions={nameFilterOptions}
            slotProps={desktopDropdownSlotProps}
            onInputChange={(_, newInputValue) => {
              if (setSearchName) {
                setSearchName(newInputValue || '');
              }
            }}
            onChange={(_, newValue) => {
              const value =
                typeof newValue === 'string'
                  ? newValue
                  : (newValue || '');
              if (setSearchName) {
                setSearchName(value);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search by worker name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.defaultMuiPrevented = true;
                    if (onSearch) onSearch();
                  }
                }}
                sx={{
                  flex: 2,
                  height: '100%',
                  '& .MuiOutlinedInput-root': {
                    height: '100%',
                    '& fieldset': { border: 'none' }
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: '#323232' }} />
                    </InputAdornment>
                  )
                }}
              />
            )}
            sx={{
              // Keep "Search by worker name" width as-is / slightly prioritized
              flex: 2,
              minWidth: 220,
              height: '100%',
              '& .MuiAutocomplete-inputRoot': {
                height: '100%'
              },
              '& .MuiInputBase-root': {
                height: '100%',
                alignItems: 'center'
              }
            }}
          />

          {/* Workers needed (desktop) */}
          {!isTabletOrDown && (
            <Box
              sx={{
                // Small numeric field (1–2 digits)
                width: 72,
                px: 1,
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                borderLeft: '1px solid #eee',
                flexShrink: 0
              }}
            >
              <TextField
                type="number"
                variant="standard"
                value={minTeamSize || 1}
                onChange={(e) => {
                  const val = e.target.value;
                  if (setMinTeamSize) setMinTeamSize(val);
                }}
                inputProps={{ min: 1, step: 1, inputMode: 'numeric' }}
                placeholder="People"
                InputProps={{ disableUnderline: true }}
                sx={{
                  width: '100%',
                  '& .MuiInputBase-root': {
                    height: '100%',
                    alignItems: 'center'
                  },
                  '& .MuiInputBase-input': {
                    padding: 0,
                    fontSize: '1rem',
                    color: '#323232',
                    textAlign: 'center'
                  }
                }}
              />
            </Box>
          )}
          <Button
            variant="contained"
            onClick={onSearch}
            sx={{
              bgcolor: '#323232',
              px: 2,
              borderRadius: 0,
              color: '#DDD0C8',
               height: '100%',
               whiteSpace: 'nowrap',
              '&:hover': { bgcolor: '#1a1a1a' }
            }}
          >
            Search
          </Button>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%', maxWidth: { xs: '100%', md: 740 } }}>
          {renderNearMeChip()}
        </Box>
      </Box>
  );

  // Compact two-row search used when the hero banner image is shown (desktop).
  const pillFieldSx = {
    bgcolor: '#fff',
    border: '1px solid #ECECEC',
    borderRadius: '10px',
    px: 1.2,
    height: 46,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  };

  const pillInputSx = {
    width: '100%',
    '& .MuiInput-underline:before': { borderBottom: 'none' },
    '& .MuiInput-underline:after': { borderBottom: 'none' },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
    '& .MuiInputBase-input': {
      padding: 0,
      fontSize: '13px',
      color: '#323232',
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#9E9E9E',
      opacity: 1,
    },
  };

  const bannerContentWidth = { xs: '100%', md: '100%' };

  const bannerSearchBar = (
    <Box
      sx={{
        width: '100%',
        p: '14px',
        borderRadius: '12px',
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      {/* Row 1: Location + Skill */}
      <Box
        sx={{
          display: 'flex',
          gap: '10px',
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Box sx={{ ...pillFieldSx, flex: 1 }}>
          <Autocomplete
            freeSolo
            options={villages || []}
            value={location || ''}
            onInputChange={(_, newInputValue) => {
              if (setLocation) setLocation(newInputValue || '');
            }}
            onChange={(_, newValue) => {
              const value = typeof newValue === 'string' ? newValue : (newValue || '');
              if (setLocation) setLocation(value);
            }}
            slotProps={desktopDropdownSlotProps}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Location"
                variant="standard"
                sx={pillInputSx}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 1 }}>
                      <LocationOn sx={{ color: '#323232' }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            sx={{
              width: '100%',
              '& .MuiAutocomplete-inputRoot': { padding: '0 !important' },
            }}
          />
        </Box>

        <Box sx={{ ...pillFieldSx, flex: 1 }}>
          <Autocomplete
            freeSolo
            options={services || []}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option?.name || ''
            }
            filterOptions={skillFilterOptions}
            value={services?.find((s) => s.name === service) || null}
            inputValue={skillInputText !== undefined ? skillInputText : (service || '')}
            onInputChange={(_, newInputValue) => {
              if (setSkillInputText) setSkillInputText(newInputValue || '');
            }}
            onChange={(_, newValue) => {
              const selected =
                typeof newValue === 'string' ? newValue : (newValue?.name || '');
              setService(selected);
              if (setSkillInputText) setSkillInputText(selected);
            }}
            slotProps={desktopDropdownSlotProps}
            renderOption={(props, option) => (
              <li {...props}>
                <SkillLabel name={option?.name || ''} />
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Skill"
                variant="standard"
                inputProps={{
                  ...params.inputProps,
                  className: `notranslate ${params.inputProps?.className || ''}`.trim(),
                  translate: 'no',
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.defaultMuiPrevented = true;
                    if (onSearch) onSearch();
                  }
                }}
                sx={pillInputSx}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 1 }}>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          gridTemplateRows: 'repeat(2, 1fr)',
                          gap: '2px',
                          width: 16,
                          height: 16,
                        }}
                      >
                        {[0, 1, 2, 3].map((i) => (
                          <Box
                            key={i}
                            sx={{ bgcolor: '#323232', borderRadius: '2px' }}
                          />
                        ))}
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />
            )}
            sx={{
              width: '100%',
              '& .MuiAutocomplete-inputRoot': { padding: '0 !important' },
            }}
          />
        </Box>
      </Box>

      {/* Row 2: Worker name + Search button */}
      <Box
        sx={{
          display: 'flex',
          gap: '10px',
          alignItems: 'stretch',
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Box sx={{ ...pillFieldSx, flex: 1 }}>
          <Autocomplete
            freeSolo
            options={workerNames || []}
            value={null}
            inputValue={searchName || ''}
            filterOptions={nameFilterOptions}
            slotProps={desktopDropdownSlotProps}
            onInputChange={(_, newInputValue) => {
              if (setSearchName) setSearchName(newInputValue || '');
            }}
            onChange={(_, newValue) => {
              const value = typeof newValue === 'string' ? newValue : (newValue || '');
              if (setSearchName) setSearchName(value);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search by worker name"
                variant="standard"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.defaultMuiPrevented = true;
                    if (onSearch) onSearch();
                  }
                }}
                sx={pillInputSx}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 1 }}>
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 18,
                          height: 18,
                          color: '#323232',
                        }}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-4 0-8 2-8 6v2h16v-2c0-4-4-6-8-6Z"
                            fill="#323232"
                          />
                        </svg>
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />
            )}
            sx={{
              width: '100%',
              '& .MuiAutocomplete-inputRoot': { padding: '0 !important' },
            }}
          />
        </Box>

        <Button
          variant="contained"
          onClick={onSearch}
          startIcon={<Search sx={{ fontSize: 16 }} />}
          sx={{
            bgcolor: HIGHLIGHT_BROWN,
            color: '#fff',
            borderRadius: '10px',
            width: { xs: '100%', sm: 110 },
            minWidth: { sm: 110 },
            height: 46,
            px: 1.25,
            whiteSpace: 'nowrap',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '15px',
            boxShadow: 'none',
            flexShrink: 0,
            '&:hover': { bgcolor: '#6d350f', boxShadow: 'none' },
          }}
        >
          Search
        </Button>
      </Box>

      {/* Near Me — opt-in GPS distance search. Requests owner location only on
          tap; normal Location/Skill/Name search never requests GPS. No map. */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        {renderNearMeChip()}
      </Box>
    </Box>
  );

  const centeredHeroText = (
    <>
      <Typography
        variant="h2"
        component="h1"
        gutterBottom
        noWrap
        sx={{
          fontWeight: 'bold',
          fontSize: { xs: '2.7rem', md: '3.8rem' },
          mb: 2,
          overflow: 'hidden',
          textOverflow: 'clip',
          maxWidth: { xs: '100%', md: '100%' },
          mx: 'auto',
        }}
      >
        <AutoFitText minFontSize={28}>{mainHeading}</AutoFitText>
      </Typography>
      <Typography
        variant="h5"
        component="p"
        noWrap
        sx={{
          mb: 1,
          fontSize: { xs: '1.3rem', md: '1.65rem' },
          fontWeight: 500,
          maxWidth: { xs: '100%', md: '75%' },
          mx: 'auto',
          overflow: 'hidden',
          textOverflow: 'clip',
        }}
      >
        <AutoFitText minFontSize={16}>{subheading}</AutoFitText>
      </Typography>
      <Typography
        variant="h6"
        component="p"
        noWrap
        sx={{
          mb: 6,
          color: '#323232',
          fontWeight: 600,
          fontSize: { xs: '0.95rem', md: '1.1rem' },
          maxWidth: { xs: '100%', md: '45%' },
          mx: 'auto',
          overflow: 'hidden',
          textOverflow: 'clip',
        }}
      >
        <AutoFitText minFontSize={12}>{smallDescription}</AutoFitText>
      </Typography>
    </>
  );

  const bannerTextBlock = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: { xs: '6px', md: '10px' },
      }}
    >
      <Typography
        component="h1"
        sx={{
          width: '100%',
          m: 0,
          textAlign: 'left',
          display: 'block',
        }}
      >
        <BannerFillHeading
          minFontSize={13}
          maxFontSize={64}
          onAfterFit={bannerHeadingParts.measurable ? measureSubheadingUnderRight : undefined}
        >
          {bannerHeadingParts.measurable ? bannerHeadingParts.nodes : renderBannerHeading(mainHeading)}
        </BannerFillHeading>
      </Typography>
      <Box
        sx={{
          width: bannerHeadingParts.measurable && subheadingEndWidth
            ? `${subheadingEndWidth}px`
            : '100%',
          maxWidth: '100%',
        }}
      >
        {bannerHeadingParts.measurable && subheadingEndWidth ? (
          <BannerFillHeading
            minFontSize={11}
            maxFontSize={20}
            targetWidthPx={subheadingEndWidth}
            fontWeight={500}
            color="#2A2A2A"
          >
            {subheading}
          </BannerFillHeading>
        ) : (
          <Typography
            component="p"
            noWrap
            sx={{
              width: '100%',
              fontSize: { xs: '12px', sm: '14px', md: '20px' },
              fontWeight: 500,
              lineHeight: 1.35,
              color: '#2A2A2A',
              m: 0,
              textAlign: 'left',
            }}
          >
            {subheading}
          </Typography>
        )}
      </Box>
      <Typography
        component="p"
        noWrap
        sx={{
          width: '100%',
          fontSize: { xs: '11px', sm: '12px', md: '14px' },
          fontWeight: 500,
          lineHeight: 1.4,
          color: '#6E6E6E',
          m: 0,
          textAlign: 'left',
        }}
      >
        {renderBannerSmallDescription(smallDescription)}
      </Typography>
    </Box>
  );

  const bannerLeftContent = (
    <Box
      sx={{
        width: '100%',
        maxWidth: bannerContentWidth,
        pr: { md: 1 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
      }}
    >
      <Box sx={{ mb: { xs: 0, md: '14px' } }}>{bannerTextBlock}</Box>
      {bannerSearchBar}
      <BannerHappyCustomersBadge />
    </Box>
  );

  // Hero text + search stack (bannerHeroText kept as alias for older references)
  const bannerHeroText = bannerLeftContent;

  const bannerImagePanel = (
    <Box
      sx={{
        position: 'relative',
        flex: { xs: '0 0 45%', md: '0 0 42%' },
        maxWidth: { xs: '45%', md: '42%' },
        minWidth: 0,
        height: 'auto',
        alignSelf: 'stretch',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-end',
        overflow: 'visible',
        ml: { md: -1 },
        pointerEvents: 'none',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: '100%',
          height: '95%',
          right: 0,
          top: 'auto',
          bottom: 0,
          borderRadius: '50%',
          background:
            'radial-gradient(ellipse at 70% 60%, rgba(255,248,242,0.9) 0%, rgba(239,228,218,0.35) 50%, rgba(239,228,218,0) 75%)',
          filter: 'blur(28px)',
          zIndex: 0,
          pointerEvents: 'none',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          flexShrink: 0,
          height: 'calc(480px - 1in)',
          maxHeight: 'calc(480px - 1in)',
          width: '100%',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'flex-start',
          overflow: 'visible',
        }}
      >
        {!heroImgLoaded && (
          <Skeleton
            variant="rounded"
            animation="wave"
            sx={{
              position: 'absolute',
              bottom: 0,
              left: '8%',
              width: '84%',
              height: '92%',
              borderRadius: 3,
              bgcolor: 'rgba(122,75,42,0.10)',
            }}
          />
        )}
        <Box
          component="img"
          src={mediaVariantUrl(bannerImageUrl, 'optimized')}
          srcSet={mediaSrcSet(bannerImageUrl)}
          sizes="(max-width: 900px) 90vw, 45vw"
          alt=""
          fetchpriority="high"
          decoding="async"
          onLoad={() => setHeroImgLoaded(true)}
          onError={() => setHeroImgLoaded(true)}
          sx={{
            width: 'auto',
            height: '100%',
            maxHeight: '100%',
            maxWidth: 'none',
            objectFit: 'contain',
            objectPosition: 'left bottom',
            transform: { xs: 'none', md: 'translateX(-4%) translateY(7%) scale(1.15)' },
            transformOrigin: 'left bottom',
            display: 'block',
            pointerEvents: 'none',
            opacity: heroImgLoaded ? 1 : 0,
            transition: 'opacity 0.4s ease',
          }}
        />
      </Box>
    </Box>
  );

  const modernHeroBannerRow = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: { xs: 'stretch', md: 'flex-end' },
        justifyContent: 'flex-start',
        width: '100%',
        height: { xs: '100%', md: 'auto' },
        gap: { xs: 0, md: 0 },
      }}
    >
      <Box
        sx={{
          flex: { xs: '0 0 55%', md: '0 0 58%' },
          maxWidth: { xs: '55%', md: '58%' },
          minWidth: 0,
          width: { md: '58%' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'flex-end',
          pl: { xs: 2, md: 0 },
          pr: { xs: 1, md: 0 },
          zIndex: 2,
        }}
      >
        {isMobile ? bannerTextBlock : bannerHeroText}
      </Box>
      {bannerImagePanel}
    </Box>
  );

  // While the banner config/image is still loading, render the heading, search
  // form and buttons IMMEDIATELY (never blank) with a skeleton standing in for
  // the banner image. The layout mirrors the loaded banner to avoid layout shift.
  if (!bannerConfigReady) {
    const bannerImageSkeleton = (
      <Box
        sx={{
          position: 'relative',
          flex: { xs: '0 0 45%', md: '0 0 42%' },
          maxWidth: { xs: '45%', md: '42%' },
          minWidth: 0,
          alignSelf: 'stretch',
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        <Skeleton
          variant="rounded"
          animation="wave"
          sx={{
            width: '100%',
            height: 'calc(480px - 1in)',
            maxHeight: 'calc(480px - 1in)',
            borderRadius: 3,
            bgcolor: 'rgba(122,75,42,0.10)',
          }}
        />
      </Box>
    );

    if (isMobile) {
      return (
        <Box sx={{ bgcolor: MOBILE_PAGE_CREAM, color: '#1E1E1E', pt: 7, pb: 2.5 }}>
          <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
            <Skeleton
              variant="rounded"
              animation="wave"
              sx={{ height: 180, borderRadius: '20px', bgcolor: 'rgba(122,75,42,0.10)', mb: 2 }}
            />
            {mobileSearchBar}
          </Container>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          background: modernHeroBackground,
          color: '#1E1E1E',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          pt: { xs: 10, md: '96px' },
          pb: { xs: 1.5, md: 0 },
          position: 'relative',
          overflow: 'hidden',
          boxSizing: 'border-box',
          minHeight: { xs: 240, md: 'calc(520px - 1in)' },
        }}
        aria-busy="true"
        aria-label="Loading homepage hero"
      >
        <Box sx={{ width: '100%', maxWidth: HERO_CONTENT_MAX_WIDTH, mx: 'auto', px: HERO_CONTENT_PX }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: { xs: 'stretch', md: 'flex-end' },
              justifyContent: 'flex-start',
              width: '100%',
            }}
          >
            <Box
              sx={{
                flex: { xs: '0 0 55%', md: '0 0 58%' },
                maxWidth: { xs: '55%', md: '58%' },
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-end',
                zIndex: 2,
              }}
            >
              {bannerHeroText}
            </Box>
            {bannerImageSkeleton}
          </Box>
        </Box>
      </Box>
    );
  }

  if (isMobile && homeSloganBannerActive && bannerImageUrl) {
    return (
      <Box
        sx={{
          bgcolor: MOBILE_PAGE_CREAM,
          color: '#1E1E1E',
          pt: 7,
          pb: 2.5,
        }}
      >
        <Container
          maxWidth="lg"
          sx={{ px: { xs: 2, sm: 3 } }}
        >
          {/* ── Mobile hero banner ── */}
          <Box
            component="section"
            aria-label="Featured workers"
            sx={{
              position: 'relative',
              height: 180,
              borderRadius: '20px',
              overflow: 'hidden',
              background: mobileBannerBackground,
              boxShadow: '0 6px 24px rgba(0,0,0,0.08)',
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            {/* Left column — text, vertically centered then nudged up a little */}
            <Box
              sx={{
                width: bannerImageUrl ? '55%' : '100%',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                pl: 2,
                pr: bannerImageUrl ? 0.5 : 2,
                // Bottom padding shifts the centered text block slightly upward
                pb: 3.5,
                zIndex: 2,
              }}
            >
              <Typography
                component="h1"
                noWrap
                sx={{
                  m: 0,
                  mb: 0.7,
                  fontWeight: 800,
                  fontSize: 'clamp(1.15rem, 5vw, 1.32rem)',
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                  color: '#000000',
                  overflow: 'visible',
                }}
              >
                {renderBannerHeading(mainHeading)}
              </Typography>

              <Typography
                component="p"
                noWrap
                sx={{
                  m: 0,
                  mb: 0.45,
                  fontSize: 'clamp(0.74rem, 3.1vw, 0.84rem)',
                  fontWeight: 700,
                  lineHeight: 1.35,
                  color: '#111111',
                  overflow: 'visible',
                }}
              >
                {subheading}
              </Typography>

              <Typography
                component="p"
                noWrap
                sx={{
                  m: 0,
                  fontSize: 'clamp(0.7rem, 2.9vw, 0.78rem)',
                  fontWeight: 600,
                  lineHeight: 1.35,
                  color: '#222222',
                  overflow: 'visible',
                }}
              >
                {renderBannerSmallDescription(smallDescription)}
              </Typography>
            </Box>

            {/* Right 45% — image fills full height (only when image is configured) */}
            {bannerImageUrl && (
              <Box
                sx={{
                  position: 'relative',
                  width: '45%',
                  flexShrink: 0,
                  height: '100%',
                  overflow: 'hidden',
                }}
              >
                {!heroImgLoaded && (
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      bgcolor: 'rgba(122,75,42,0.10)',
                    }}
                  />
                )}
                <Box
                  component="img"
                  src={mediaVariantUrl(bannerImageUrl, 'medium')}
                  srcSet={mediaSrcSet(bannerImageUrl)}
                  sizes="100vw"
                  alt=""
                  fetchpriority="high"
                  decoding="async"
                  onLoad={() => setHeroImgLoaded(true)}
                  onError={() => setHeroImgLoaded(true)}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'top center',
                    display: 'block',
                    pointerEvents: 'none',
                    opacity: heroImgLoaded ? 1 : 0,
                    transition: 'opacity 0.4s ease',
                  }}
                />
              </Box>
            )}

            {/* Happy Customers trust badge — overlaid inside the banner card */}
            <Box
              sx={{
                position: 'absolute',
                left: 8,
                bottom: 8,
                zIndex: 3,
              }}
            >
              <BannerHappyCustomersBadge compact />
            </Box>
          </Box>

          {mobileSearchBar}
        </Container>
      </Box>
    );
  }

  if (showModernHeroBanner) {
    return (
      <Box
        sx={{
          background: modernHeroBackground,
          color: '#1E1E1E',
          minHeight: { xs: 'auto', md: 'auto' },
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          pt: { xs: 10, md: '96px' },
          pb: { xs: 1.5, md: 0 },
          position: 'relative',
          overflow: 'hidden',
          scrollMarginTop: '80px',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: HERO_CONTENT_MAX_WIDTH,
            mx: 'auto',
            px: HERO_CONTENT_PX,
          }}
        >
          {modernHeroBannerRow}
        </Box>
      </Box>
    );
  }

  if (isMobile) {
    return mobileHeroShell;
  }

  return (
    <Box
      sx={{
        background: '#DDD0C8',
        color: '#323232',
        py: { xs: 6, md: 10 },
        textAlign: 'center',
        position: 'relative',
      }}
    >
      <Container maxWidth="lg">
        {centeredHeroText}
        {desktopSearchBar}
      </Container>
    </Box>
  );
};

export default HomeHeroSearch;
